/*
 * lazy-videos.js — Carga diferida de vídeos (Tech Specs: vídeo de escritorio + carrusel móvil)
 *
 * Marcado esperado:
 *   <video data-lazy-video data-webm="URL.webm" data-mp4="URL.mp4"
 *          muted loop playsinline webkit-playsinline preload="none"></video>
 *
 *   - El <video> NO lleva src ni <source>: no se descarga nada hasta que este script lo decide.
 *   - Solo se cargan los vídeos que realmente se renderizan (un vídeo dentro de un
 *     contenedor con display:none —p. ej. el vídeo de escritorio en móvil— nunca se descarga).
 *   - Se empiezan a cargar cuando la sección está a ~1 pantalla de distancia
 *     (data-lazy-group en el contenedor de la sección).
 *   - Dentro de un carrusel horizontal (data-lazy-scroller) solo se cargan el slide
 *     visible y los cercanos; el resto, a medida que el usuario desliza.
 *   - Solo se reproduce lo que está en pantalla; se pausa al salir.
 *
 * Formato: WebM (VP9) en navegadores no Apple; MP4 en Safari / cualquier navegador de iOS
 * (en iOS todos usan WebKit) y como respaldo si no hay WebM.
 */
(function () {
  'use strict';

  var isApple = navigator.vendor === 'Apple Computer, Inc.';
  var canWebm = !isApple &&
    document.createElement('video').canPlayType('video/webm; codecs="vp9"') === 'probably';

  /** Devuelve la URL adecuada para este navegador. */
  function pick(mp4, webm) {
    if (webm && canWebm && !/\.mp4($|\?)/i.test(webm)) return webm;
    return mp4 || webm || null;
  }

  function isRendered(el) {
    return el.getClientRects().length > 0;
  }

  function safePlay(v) {
    var p = v.play();
    if (p && p.catch) p.catch(function () { /* autoplay bloqueado (p. ej. modo bajo consumo) */ });
  }

  /** Asigna la fuente a un vídeo (una sola vez). Devuelve true si ha asignado. */
  function attach(v) {
    if (v.dataset.loaded) return false;
    if (!isRendered(v)) return false;
    var src = pick(v.dataset.mp4, v.dataset.webm);
    if (!src) return false;
    v.dataset.loaded = '1';
    v.preload = 'auto';
    v.src = src;
    return true;
  }

  /** Cambia la fuente de un vídeo (lo usa specs-videos.js al pasar el ratón por los puntos). */
  function setSource(v, mp4, webm) {
    var src = pick(mp4, webm);
    if (!src) return false;
    v.dataset.loaded = '1';
    v.preload = 'auto';
    if (v.getAttribute('src') !== src && v.src !== src) {
      v.src = src;
      v.load();
    }
    return true;
  }

  window.UptakVideos = { pick: pick, attach: attach, setSource: setSource, play: safePlay };

  // Navegadores sin IntersectionObserver (muy antiguos): cargar lo visible al terminar la página
  if (!('IntersectionObserver' in window)) {
    window.addEventListener('load', function () {
      document.querySelectorAll('video[data-lazy-video]').forEach(function (v) { if (attach(v)) safePlay(v); });
    });
    return;
  }

  var allVideos = [];                 // todos los vídeos gestionados
  var visible = new WeakMap();        // vídeo -> está en pantalla
  var nearInScroller = new WeakMap(); // vídeo -> cerca dentro de su carrusel
  var openGroups = new WeakSet();     // secciones a las que el usuario ya se ha acercado

  function groupOf(v) { return v.closest('[data-lazy-group]'); }
  function scrollerOf(v) { return v.closest('[data-lazy-scroller]'); }

  function groupIsOpen(v) {
    var g = groupOf(v);
    return !g || openGroups.has(g);
  }

  function evaluate(v) {
    if (!v.dataset.loaded) {
      var wanted = visible.get(v) ||
        (groupIsOpen(v) && (!scrollerOf(v) || nearInScroller.get(v)));
      if (wanted) attach(v);
    }
    if (v.dataset.loaded) {
      if (visible.get(v)) { if (v.paused) safePlay(v); }
      else if (!v.paused) v.pause();
    }
  }

  // 1) Secciones: se "abren" cuando están a ~1 pantalla de distancia
  var groupObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      openGroups.add(e.target);
      groupObserver.unobserve(e.target);
      e.target.querySelectorAll('video[data-lazy-video]').forEach(evaluate);
    });
  }, { rootMargin: '100% 0px 100% 0px' });

  // 3) Reproducción: solo lo que está en pantalla
  var playObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      visible.set(e.target, e.isIntersecting);
      evaluate(e.target);
    });
  }, { threshold: 0.2 });

  /** Registra los vídeos de un fragmento del DOM (la página, o una sección recargada en el editor). */
  function observeRoot(root) {
    if (root.matches && root.matches('[data-lazy-group]')) groupObserver.observe(root);
    root.querySelectorAll('[data-lazy-group]').forEach(function (g) { groupObserver.observe(g); });

    // 2) Carruseles horizontales: slide visible + vecinos (una anchura de carrusel a cada lado)
    root.querySelectorAll('[data-lazy-scroller]').forEach(function (scroller) {
      var so = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          nearInScroller.set(e.target, e.isIntersecting);
          evaluate(e.target);
        });
      }, { root: scroller, rootMargin: '0px 100% 0px 100%' });
      scroller.querySelectorAll('video[data-lazy-video]').forEach(function (v) { so.observe(v); });
    });

    root.querySelectorAll('video[data-lazy-video]').forEach(function (v) {
      allVideos.push(v);
      playObserver.observe(v);
    });
  }

  observeRoot(document);

  // 4) iOS en modo bajo consumo bloquea el autoplay: con el primer gesto del usuario (toque/clic)
  //    se "desbloquean" todos los vídeos ya cargados (play y, si no están en pantalla, pausa),
  //    para que después puedan reproducirse solos al deslizar el carrusel.
  function unlockFromGesture() {
    allVideos.forEach(function (v) {
      if (!v.isConnected || !v.dataset.loaded || !v.paused) return;
      var p = v.play();
      if (!visible.get(v) && p && p.then) {
        p.then(function () { if (!visible.get(v)) v.pause(); }).catch(function () {});
      } else if (p && p.catch) {
        p.catch(function () {});
      }
    });
  }
  window.addEventListener('touchend', unlockFromGesture, { passive: true });
  window.addEventListener('click', unlockFromGesture, { passive: true });

  // 5) Al volver con "atrás" (página restaurada de caché) reanudar lo que está en pantalla
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    allVideos.forEach(function (v) { if (v.isConnected) evaluate(v); });
  });

  // 6) Editor de temas de Shopify: al recargar una sección, registrar sus vídeos nuevos
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) observeRoot(e.target);
  });
})();
